


































































































































































































import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import {
    DxDataGrid,
    DxLookup,
    DxColumn,
    DxEditing,
    DxPopup,
    DxForm,
    DxPatternRule,
} from 'devextreme-vue/data-grid';
import { DxItem } from 'devextreme-vue/form';
import {
    TableConfig,
    defaultTableConfig,
    TableColumn,
    columnTemplates,
} from '@/core/components/table';
import {
    CONTRACTS,
    WindowsServiceContract,
    FetchServiceContract,
    EventsServiceContract,
    Events
} from '@/core/contracts';
import { Inject } from '@/core/helpers/vue-inversify-decorator';
import { Table } from '@/core/components/table/types';
import { ContextsState } from '@/store';
import DataSource from 'devextreme/data/data_source';
import { guid } from '@/core/helpers';
import { localize } from '@/core/filters';
import { IContext, ITech, UsageResourceType } from '@/api';
import { TreeDropDown } from '@/core/components/controls';
import { ResourceTypeTree } from '@/dto';
import { getParentId } from '@/core/components/tree/treeHelpers';
import { CalculationTypes } from './types';
import UsageResourceTypesDropDownButton from './Components/UsageResourceTypesDropDownButton.vue';

@Component({
    components: {
        DxDataGrid,
        DxLookup,
        DxColumn,
        DxEditing,
        DxPopup,
        DxForm,
        DxItem,
        DxPatternRule,
        TreeDropDown,
        UsageResourceTypesDropDownButton
    },
})
export default class TechesListTable extends Vue {
    @Prop({ type: String, required: true }) public tableKey!: string;

    public dataSource = new DataSource({
        key: 'id' as keyof ITech,
        load: this.load,
        insert: (tech: ITech) => this.insert(tech),
        update: (id: string, data: ITech) => this.update(id, data),
    });

    @Inject(CONTRACTS.WindowsService)
    private _windowsService?: WindowsServiceContract;
    @Inject(CONTRACTS.FetchService)
    private _fetchService?: FetchServiceContract;
    @Inject(CONTRACTS.EventsService)
    private _eventsService?: EventsServiceContract;
    private techesCount: number = 0;
    private resourceTypes: any[] | undefined = [];
    private sites: any[] | undefined = [];
    private techTypes: string[] = ['Load', 'Unload'];
    private durationPattern = /(?:(\d+)\.)?([0-1][0-9]|2[0-3]):([0-5][0-9]):([0-5][0-9])/;

    public get tableConfig(): Partial<TableConfig> {
        return {
            ...defaultTableConfig(this),
            onInitNewRow: e => this.setDefaultData(e.data),
        };
    }

    public async mounted() {
        // raise instance of the table up to parent
        this.$emit('setTableInstance', this.tableInstance);
        this._eventsService!.on(
            Events.ProcessingFinished,
            this.onProcessingFinished
        );
    }

    public async beforeDestroy() {
        this._eventsService!.off(
            Events.ProcessingFinished,
            this.onProcessingFinished
        );
    }

    public get tableInstance(): Table {
        const element = this.$refs['data-grid'] as unknown;
        return (element as DxDataGrid).instance!;
    }

    public get columnTemplates() {
        return columnTemplates;
    }

    public deleteTech() {
        this._windowsService!.showDialog(localize('Tech_DeleteMessage'), [
            { id: 1, caption: localize('DialogYes') },
            { id: 2, caption: localize('DialogCancel') },
        ]).then(id => {
            if (id === 1) {
                const tech = this.tableInstance.getSelectedRowsData()[0];
                const data = {
                    Teches_Partial: [
                        {
                            id: tech.id,
                            IsDeleted: true,
                        },
                    ],
                };
                const currentContextId = ContextsState.currentContextId;
                const url = `/files/contexts/${currentContextId}?ignoreWarnings=true`;
                this._fetchService!.post<ITech>(
                    url,
                    JSON.stringify(data)
                ).then(() => this.getDataGridInstance().refresh());
            }
        });
    }

    private onProcessingFinished({ contextId }: any) {
        if (Number(ContextsState.currentContextId) === Number(contextId)) {
            this.tableInstance.refresh();
        }
    }

    private setDefaultData(tech: ITech) {
        tech.name = 'Tech' + this.techesCount;
        const nullableDuration = '0.00:00:00';
        tech.duration = nullableDuration;
        tech.preparationTime = nullableDuration;
        tech.liftTime = nullableDuration;
        tech.completionTime = nullableDuration;
        tech.changePlaceTime = nullableDuration;
        tech.liftFactor = 1;
    }

    private async load() {
        const currentContextId = ContextsState.currentContextId;
        this.resourceTypes = await this
            ._fetchService!.get<IContext>(`/files/contexts/${currentContextId}/ResourceTypes`)
            .then(items => items.resourceTypes);
        this.sites = await this
            ._fetchService!.get<IContext>(`/files/contexts/${currentContextId}/Sites`)
            .then(items => items.sites);
        const data = await this
            ._fetchService!.get<IContext>(`/files/contexts/${currentContextId}/Teches`)
            .then(items => this.transform(items));

        if (!data.teches) {
            return [];
        }
        return data.teches;
    }

    private async insert(tech: ITech) {
        const currentContextId = ContextsState.currentContextId;
        tech = this.prepare(tech);
        const url = `/files/contexts/${currentContextId}?ignoreWarnings=true`;
        tech.id = guid();
        const data = {
            Teches_Partial: [tech]
        };
        await this._fetchService!.post<ITech>(url, JSON.stringify(data));
    }

    private async update(id: string, tech: ITech) {
        const currentContextId = ContextsState.currentContextId;
        const url = `/files/contexts/${currentContextId}?ignoreWarnings=true`;
        tech.id = id;
        tech = this.prepare(tech);
        const data = {
            Teches_Partial: [tech]
        };
        return await this._fetchService!.post<ITech>(url, JSON.stringify(data));
    }

    private async transform(data: IContext) {
        this.techesCount = data.teches?.length ?? 0;
        data.teches?.forEach(element => {
            element.category = (element.category as string)
                .split(',')
                .map(item => item.trim());
            if (element.usageResourceTypes) {
                element.usageResourceTypes = (element.usageResourceTypes as string)
                .split(';')
                .map((item: string) => {
                    const value = item.trim().split(':');
                    return {
                        resourceType: value[0],
                        multitaskingCoefficient: Number(value[1])
                    } as unknown as UsageResourceType;
                });
            }
        });
        return data;
    }

    private getCellValue(rowInfo: any) {
        return rowInfo.usageResourceTypes?.map((item: UsageResourceType) => item.resourceType).join(',');
    }

    private getDisplayValue(rowInfo: any) {
        return rowInfo.usageResourceTypes?.map((item: UsageResourceType) => {
            return this.resourceTypes?.find(resource => resource.id === item.resourceType).name +
            ' (' + item.multitaskingCoefficient + ')';
        }).join(', ');
    }

    private initNewRow(row: any) {
        row.data.usageResourceTypes = [];
    }

    private get localizePlaceholder() {
        return localize('PlaceholderAny');
    }

    public get localize() {
        return localize;
    }

    private get getCalculationTypes() {
        const items: any[] = [];

        for (const item in CalculationTypes) {
            if (!isNaN(Number(item))) {
                items.push({
                    name: this.localize('Tech_' + CalculationTypes[item]),
                    value: Number(item)
                });
            }
        }

        return items;
    }

    private prepare(tech: ITech): ITech {
        tech.category = (tech.category as string[])?.join(', ');
        tech.usageResourceTypes = (tech.usageResourceTypes as UsageResourceType[])?.map(item => {
            return item.resourceType + ':' + item.multitaskingCoefficient;
        })?.join(';');
        return tech;
    }

    private getDataGridInstance(): Table {
        const element = this.$refs['data-grid'] as unknown;
        return (element as DxDataGrid).instance!;
    }

    private getloadHandler(): (parameters: any) => Promise<any[]> {
        return (parameters: any) => {
            return this.treeDropDownLoad(parameters);
        };
    }

    private async treeDropDownLoad(parameters: any) {
        const currentContextId = ContextsState.currentContextId;
        const parentId = parameters && parameters.filter ? getParentId(parameters.filter) : '';
        const url = `/contexts/${currentContextId}/resourceTypesList/${parentId}`;
        const data = await this._fetchService!.get<ResourceTypeTree[]>(url);
        return this.treeDropDownTransform(data, parentId);
    }

    private getloadSelectedItemsHandler(): (items: any) => Promise<any[]> {
        return (items: any) => {
            return this.loadSelectedItems(items);
        };
    }

    private async loadSelectedItems(items: any) {
        const currentContextId = ContextsState.currentContextId;
        const url = `/contexts/${currentContextId}/resourceTypesList/selected/${items}`;
        const data = await this._fetchService!.get<ResourceTypeTree[]>(url);

        return data;
    }

    private treeDropDownTransform(data: ResourceTypeTree[], parentId: string) {
        return data.map((item: ResourceTypeTree) => {
            item.parentId = parentId;
            return item;
        });
    }

    private itemsArrayToString(items: any) {
        if (!items || items === null) {
            return '';
        }
        return typeof items === 'string' ? items : items.join(',');
    }
}
